import { useEffect, useRef } from "react";

const STORAGE_PREFIX = "redo:fulfillment-group-scroll:";

/**
 * Locate the virtual table's internal scroll container. `RedoVirtualTable`
 * tags it with `data-scroll-container="virtual-table"` so we can find it
 * without depending on a CSS-module class name — the production build hashes
 * those into short, content-free identifiers that defeat substring matching.
 */
function findVirtualScrollContainer(): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    '[data-scroll-container="virtual-table"]',
  );
}

/**
 * Preserves the listing's scroll position across navigations to and from an
 * order-detail page (OMS-3071 follow-up). Persists the virtual table's
 * scrollTop in sessionStorage (keyed by view name) and restores it once after
 * rows have rendered. Browser back, the secondary-nav "Ready to ship" jump,
 * and any other return path all hit this on mount.
 *
 * Quirk: when the listing unmounts on row click, the virtual table's content
 * height briefly collapses and fires a synthetic `scroll → 0` event before
 * the React cleanup runs. Naïvely persisting every scroll value clobbers the
 * stored position with 0 right before the user navigates away. The handler
 * therefore skips writes when the new value is 0 — teardown noise is dropped,
 * the persisted position survives, and a user who genuinely scrolled to the
 * top before clicking will see the prior scroll restored on return (minor
 * papercut accepted to keep the logic simple).
 */
export function useFulfillmentScrollRestoration(
  viewName: string | undefined,
  rowsLength: number,
): void {
  // Tracks the last view name we restored for. Keyed by view (not a plain
  // boolean) so switching between Ready to ship / Priority / etc. within the
  // same component instance still triggers a fresh restore. Today the parent
  // remounts the children on view switch via a `<Fragment key>` trick, which
  // would also reset a boolean ref — but this hook shouldn't depend on that
  // implementation detail of an unrelated context provider.
  const restoredViewRef = useRef<string | undefined>(undefined);

  // Restore once per view after rows are populated. The virtualizer needs a
  // matching content height before scrollTop is meaningful — otherwise the
  // assignment clamps to 0.
  useEffect(() => {
    if (!viewName || rowsLength === 0 || restoredViewRef.current === viewName) {
      return;
    }
    const el = findVirtualScrollContainer();
    if (!el) {
      return;
    }
    restoredViewRef.current = viewName;
    let stored: string | null;
    try {
      stored = sessionStorage.getItem(STORAGE_PREFIX + viewName);
    } catch {
      return;
    }
    if (stored === null) {
      return;
    }
    const top = Number(stored);
    if (Number.isFinite(top) && top > 0) {
      el.scrollTop = top;
    }
  }, [viewName, rowsLength]);

  // Persist scrollTop on every meaningful scroll. Synchronous writes are
  // cheap, and dropping 0s avoids the teardown-clobber described above.
  useEffect(() => {
    if (!viewName) {
      return;
    }
    const el = findVirtualScrollContainer();
    if (!el) {
      return;
    }
    const key = STORAGE_PREFIX + viewName;
    const onScroll = () => {
      const top = el.scrollTop;
      if (top <= 0) {
        return;
      }
      try {
        sessionStorage.setItem(key, String(top));
      } catch {
        // sessionStorage can throw if disabled or full; non-fatal.
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
    };
  }, [viewName, rowsLength]);
}
